/**
 * Type Definitions for Rental Tracking System
 *
 * This file defines all the TypeScript interfaces for the rental equipment
 * tracking application, ensuring type safety throughout the codebase.
 */

// ============================================
// RESERVATION TYPES
// ============================================

/** Possible statuses for a reservation slot */
export type ReservationStatus = 'available' | 'reserved' | 'rented' | 'pickup' | 'down' | 'transferred';

/** Yard operations status */
export type YardStatus = 'pending_prep' | 'prepped' | 'on_rent' | 'returned_pending_close';

/** Delivery tracking status (for trucking calendar) */
export type DeliveryStatus =
  | 'pending'           // Default when delivery selected
  | 'scheduled'         // Trucking company & pickup time assigned
  | 'picked_up'         // Trucking company has the item
  | 'delivered';        // Item delivered to customer (or returned to yard)

/** Fuel levels for check-in/check-out */
export type FuelLevel = 'Empty' | '1/8' | '1/4' | '3/8' | '1/2' | '5/8' | '3/4' | '7/8' | 'Full';

/** Yard photo metadata */
export interface YardPhoto {
  url: string;
  timestamp: number;
  user: string;
}

// ============================================
// ARCHIVE TYPES
// ============================================

/** Rental archive record (from completed rentals) */
export interface RentalArchiveRecord {
  id: string;
  uploadType?: 'rental';          // Optional for backward compat (undefined = rental)
  equipmentId: string;
  description: string;
  reservation: ReservationDetails;
  archivedAt: string;
  archivedBy: string;
  locationId: string;
  status?: ReservationStatus;     // Reservation status at archival time
  linkedEquipmentIds?: string[];  // Flat array of all group equipment IDs (for array-contains search)
  linkedItemDetails?: Array<{ equipmentId: string; description: string }>; // For display on archive card
}

/** Manual archive record (direct photo uploads) */
export interface ManualArchiveRecord {
  id: string;
  uploadType: 'manual';           // Discriminator field
  equipmentId: string;            // REQUIRED - equipment identifier
  description?: string;           // Optional equipment description
  notes: string;                  // REQUIRED - documentation
  photos: YardPhoto[];            // REQUIRED - at least 1 photo
  uploadedAt: string;             // ISO timestamp
  archivedAt?: string;            // Query compatibility (mirrors uploadedAt)
  uploadedBy: string;             // User email
  locationId: string;             // Location where uploaded
}

/** Union type for all archive record types */
export type ArchiveRecord = RentalArchiveRecord | ManualArchiveRecord;

/** Details for a reservation (all fields optional as different statuses use different fields) */
export interface ReservationDetails {
  /**
   * Stable short identifier for this reservation instance, injected when a
   * reservation is created. Used as the parent key for the reservation's audit
   * log node (auditLogs/{board}/{env}/{loc}/{itemId}/{reservationId}). Occupied
   * sets that predate this field (not yet backfilled by the migration script)
   * may be missing it — client code generates one lazily on the next edit.
   */
  reservationId?: string;

  // Type field (for 'reserved', 'rented', and 'pickup' statuses)
  type?: 'rental' | 'demo' | 'loaner' | 'rental-purchase';
  salesman?: string; // For demo type
  rentalPeriod?: 'day' | 'week' | 'month';

  // Rental fields (for 'reserved' and 'rented' status)
  companyName?: string;
  accountNumber?: string;
  contactName?: string;
  contactPhone?: string;
  email?: string;
  poNumber?: string;
  jobName?: string;
  pickupDelivery?: 'pickup' | 'delivery' | 'swap' | 'na';
  swapDirection?: 'store-first' | 'customer-first';  // Only used when pickupDelivery='swap'
  pickupCharge?: string;
  deliveryCharge?: string;
  deliveryAddress?: string;
  jobsiteContactName?: string;
  jobsiteContactPhone?: string;

  // Delivery tracking fields (for trucking calendar)
  deliveryTruckingCompanyId?: string;   // Reference to trucking company (if from list)
  deliveryTruckingCompanyName?: string; // Denormalized name or custom entry
  deliveryPickupDate?: string;          // Date trucking company picks up (ISO: "2025-01-15")
  deliveryPickupTime?: string;          // Time for pickup (24h: "08:00", "14:30")
  deliveryStatus?: DeliveryStatus;      // Current status of delivery
  deliveryNotes?: string;               // Notes specific to this delivery

  pickupDate?: string;
  returnDate?: string;
  hours?: string;
  discountPercent?: string;

  // Down fields (for 'down' status)
  dateDowned?: string;
  workOrder?: string; // Also used for loaner type
  estCompletionDate?: string;

  // Transferred fields (for 'transferred' status)
  storeNumber?: string;
  dateTransferred?: string;

  // Yard Ops Fields
  yardStatus?: YardStatus;
  startHours?: string;
  endHours?: string;
  fuelLevelOut?: FuelLevel;
  fuelLevelIn?: FuelLevel;
  yardNotes?: string;
  noteToYard?: string; // Note from salesman to yard
  prepPhotos?: YardPhoto[];
  returnPhotos?: YardPhoto[];

  // Cross-Location Return Fields
  returnedToLocation?: string; // Physical location where item was returned (e.g., '11')
  returnedToLocationName?: string; // Human-readable name (e.g., 'Henderson')

  // Common field (all statuses)
  notes?: string;

  // Legacy fields for backward compatibility
  company?: string;
  contact?: string;
}

/** Action recorded by an audit log entry */
export type AuditAction = 'created' | 'edited' | 'cleared' | 'synced-from-parent';

/** Single audit log entry attached to a reservation set */
export interface AuditEntry {
  userEmail: string;
  timestamp: number;
  action: AuditAction;
  changes: Record<string, { from: unknown; to: unknown }>;
  syncedFromItemId?: string;
}

/** A single reservation slot (one of 4 per item) */
export interface ReservationSet {
  status: ReservationStatus;
  details: ReservationDetails | string; // string for legacy support
  auditLog?: AuditEntry[];
}

// ============================================
// ITEM FLAGS TYPES
// ============================================

/**
 * Predefined item flags for common equipment conditions.
 * Add more as needed - just update this type and the config file.
 */
export type PredefinedItemFlag = 'dirty' | 'needs-service' | 'track-service';

/**
 * Item flags structure for marking equipment conditions without affecting reservations.
 * These are item-level properties, not tied to any specific reservation slot.
 */
export interface ItemFlags {
  /** Active predefined flags */
  predefined?: PredefinedItemFlag[];
  /** Custom user-defined tags (e.g., "Missing keys", "Broken mirror") */
  customTags?: string[];
  /** Free-form notes for detailed information */
  notes?: string;
  /** Last service date (ISO format "2025-01-15") - only shown when track-service flag is enabled */
  lastServicedDate?: string;
  /** Hour meter reading at last service (machine hours) */
  lastServicedHours?: number;
  /**
   * Per-item custom flags with active state.
   * Key = flag label (user-typed), Value = active state (true = checked, false = unchecked).
   * Unlike predefined flags, custom flags persist even when unchecked until explicitly deleted.
   * Example: { "Low Fuel": true, "VIP Customer": false }
   */
  customFlags?: Record<string, boolean>;
}

// ============================================
// ITEM TYPES
// ============================================

/** Type discriminator for different item types */
export type ItemType = 'item' | 'header' | 'separator' | 'multiples';

/** Base interface that all rental items share */
interface BaseRentalItem {
  id: string;
  category: string;
  sortOrder: number;
  type: ItemType;
}

/** Linked items information */
interface LinkedItemsInfo {
  groupId?: string;
  parentId?: string;
  childIds?: string[];
  isParent?: boolean;
}

/** A rental equipment item (e.g., forklift, excavator) */
export interface RentalEquipmentItem extends BaseRentalItem {
  type: 'item';
  description: string;
  equipmentId: string;
  /**
   * @deprecated RTDB efficiency migration (D4/Fix 3): rich text is now
   * HTML-only at rest. The client no longer writes this field. Read paths
   * no longer fall back to it either — HTML (`description`) is a verified
   * superset. Left in the type only until the migration script has run
   * against production and this field is removed (see §7 of
   * docs/RTDB_EFFICIENCY_MIGRATION.md).
   */
  descriptionJSON?: string; // EditorState JSON format (legacy)
  /** @deprecated See `descriptionJSON` — same rationale, for `equipmentId`. */
  equipmentIdJSON?: string; // EditorState JSON format (legacy)
  sets: [ReservationSet, ReservationSet, ReservationSet, ReservationSet];
  linkedItems?: LinkedItemsInfo;
  /** Item-level flags for equipment conditions (dirty, needs service, etc.) */
  itemFlags?: ItemFlags;
}

/** A header row (visual separator with custom text in columns) */
export interface HeaderItem extends BaseRentalItem {
  type: 'header';
  content: [string, string, string, string, string, string];
  /**
   * @deprecated RTDB efficiency migration (D4/Fix 3): rich text is now
   * HTML-only at rest. The client no longer writes this field; see
   * `descriptionJSON` above for full rationale.
   */
  contentJSON?: [string, string, string, string, string, string]; // EditorState JSON format (legacy)
}

/** A separator row (horizontal line) */
export interface SeparatorItem extends BaseRentalItem {
  type: 'separator';
}

// ============================================
// MULTIPLES (BULK INVENTORY) TYPES
// ============================================

/** A single reservation on a multiples item */
export interface MultiplesReservation {
  quantity: number;
  status: ReservationStatus;
  groupId: string;
  details: ReservationDetails;
}

/** A bulk inventory item (e.g., ties, plates, clips) */
export interface MultiplesItem extends BaseRentalItem {
  type: 'multiples';
  description: string;
  /**
   * @deprecated RTDB efficiency migration (D4/Fix 3): rich text is now
   * HTML-only at rest. The quill editor's 'item' save path (reused for
   * multiples) no longer writes this field; see `descriptionJSON` on
   * `RentalEquipmentItem` for full rationale.
   */
  descriptionJSON?: string; // EditorState JSON format (legacy)
  totalQuantity: number;
  reservations?: Record<string, MultiplesReservation>;
}

/** Union type representing any possible rental item */
export type AnyRentalItem = RentalEquipmentItem | HeaderItem | SeparatorItem | MultiplesItem;

// ============================================
// CATEGORY TYPES
// ============================================

/** All possible equipment categories */
export type CategoryType = 'ctl' | 'skids' | 'buckets' | 'skid_attachments' | 'mt' | 'minix' | 'minix_buckets' | 'minix_attachments' | 'misc';


/**
 * Map of all data organized by category.
 * Uses string keys to support dynamic categories loaded from Firestore.
 * Default categories still use CategoryType IDs for backward compatibility.
 */
export type AllDataMap = Record<string, AnyRentalItem[]>;

// ============================================
// DYNAMIC CATEGORY CONFIGURATION
// ============================================

/**
 * Rental category definition (stored in Firestore per-location).
 * Allows different locations to have different equipment categories.
 */
export interface RentalCategory {
  /** Unique identifier for the category (e.g., 'ctl', 'skids', 'custom_trucks') */
  id: string;
  /** Human-readable display name (e.g., 'CTL', 'Skids', 'Custom Trucks') */
  displayName: string;
  /** Sort order for tab display (0 = first) */
  sortOrder: number;
  /** Category type: 'standard' for serialized equipment, 'multiples' for bulk items */
  categoryType?: 'standard' | 'multiples';
}

/**
 * Rental category configuration document (Firestore structure)
 */
export interface RentalCategoryConfig {
  categories: RentalCategory[];
}

/**
 * Default categories - used when a location has no configured categories.
 * These match the original hardcoded categories for backward compatibility.
 */
export const DEFAULT_RENTAL_CATEGORIES: RentalCategory[] = [
  { id: 'ctl', displayName: 'CTL', sortOrder: 0 },
  { id: 'skids', displayName: 'Skids', sortOrder: 1 },
  { id: 'buckets', displayName: 'Skid Buckets', sortOrder: 2 },
  { id: 'skid_attachments', displayName: 'Skid Attachments', sortOrder: 3 },
  { id: 'mt', displayName: 'MT', sortOrder: 4 },
  { id: 'minix', displayName: 'MiniX', sortOrder: 5 },
  { id: 'minix_buckets', displayName: 'MiniX Buckets', sortOrder: 6 },
  { id: 'minix_attachments', displayName: 'MiniX Attachments', sortOrder: 7 },
  { id: 'misc', displayName: 'Misc', sortOrder: 8 },
];

// ============================================
// UI STATE TYPES
// ============================================


/** Data for the currently-being-edited item */
export interface CurrentEditData {
  docId: string;
  item: RentalEquipmentItem;
  setIndex: number;
}


// ============================================
// SEARCH & NAVIGATION TYPES
// ============================================



// ============================================
// ACTION ITEMS TYPES
// ============================================


// ============================================
// DRAG & DROP TYPES
// ============================================


// ============================================
// COLOR CONFIGURATION TYPES
// ============================================

/** Status colors mapping (Tailwind classes) */
export interface StatusColors {
  [key: string]: string;
}

// ============================================
// USER & LOCATION TYPES
// ============================================

/**
 * A timestamp value as it may appear in Firestore-sourced documents:
 * a Firestore Timestamp-like object, a millisecond number, an ISO string,
 * a serverTimestamp() sentinel (object), or null.
 */
export type FirestoreTimestampValue =
  | number
  | string
  | { seconds: number; nanoseconds?: number; toMillis?: () => number }
  | { _seconds: number; _nanoseconds?: number }
  | null;

/** User role types - Global roles */
export type UserRole = 'admin' | 'user';

/** Location-specific permission level (for users only, admins have full access everywhere) */
type LocationPermissionLevel = 'viewer' | 'editor';

/** Location permission details */
export interface LocationPermission {
  role: LocationPermissionLevel;
  addedBy: string;
  addedAt: FirestoreTimestampValue;
}


/** Per-page location permissions */
interface PagePermissions {
  rentals?: {
    [locationId: string]: LocationPermission;
  };
  vendors?: {
    [locationId: string]: LocationPermission;
  };
  sales?: {
    [locationId: string]: LocationPermission;
  };
  'label-printer'?: {
    [locationId: string]: LocationPermission;
  };
}

/** User document structure in Firestore */
export interface UserDocument {
  email: string;
  displayName: string;
  role: UserRole;
  // New granular per-page permissions
  pagePermissions?: PagePermissions;
  // Legacy field for backward compatibility (rentals only)
  locationPermissions?: {
    [locationId: string]: LocationPermission;
  };
  defaultLocation?: string;
  addedBy?: string;
  addedAt?: FirestoreTimestampValue;
  lastLogin?: FirestoreTimestampValue;
}

// ============================================
// TYPE GUARDS
// ============================================

/** Type guard to check if item is rental equipment */
export function isRentalEquipmentItem(item: AnyRentalItem): item is RentalEquipmentItem {
  return item.type === 'item';
}

/** Type guard to check if item is a header */
export function isHeaderItem(item: AnyRentalItem): item is HeaderItem {
  return item.type === 'header';
}


/** Type guard to check if item is a multiples (bulk inventory) item */
export function isMultiplesItem(item: AnyRentalItem): item is MultiplesItem {
  return item.type === 'multiples';
}

/** Compute available quantity for a multiples item */
export function getMultiplesAvailable(item: MultiplesItem): number {
  if (!item.reservations) return item.totalQuantity;
  const activeStatuses: ReservationStatus[] = ['reserved', 'rented', 'pickup', 'down', 'transferred'];
  let reserved = 0;
  for (const res of Object.values(item.reservations)) {
    if (activeStatuses.includes(res.status)) {
      reserved += res.quantity;
    }
  }
  return item.totalQuantity - reserved;
}

/** Compute total reserved quantity (status = reserved) for a multiples item */
export function getMultiplesReservedCount(item: MultiplesItem): number {
  if (!item.reservations) return 0;
  let count = 0;
  for (const res of Object.values(item.reservations)) {
    if (res.status === 'reserved') count += res.quantity;
  }
  return count;
}

/** Compute total rented quantity (status = rented) for a multiples item */
export function getMultiplesRentedCount(item: MultiplesItem): number {
  if (!item.reservations) return 0;
  let count = 0;
  for (const res of Object.values(item.reservations)) {
    if (res.status === 'rented') count += res.quantity;
  }
  return count;
}

/** Type guard to check if details is an object (not legacy string) */
export function isDetailsObject(details: ReservationDetails | string): details is ReservationDetails {
  return typeof details === 'object';
}

/** Type guard to check if archive record is a manual upload */
export function isManualArchiveRecord(record: ArchiveRecord): record is ManualArchiveRecord {
  return record.uploadType === 'manual';
}

